import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { FallbackProps } from "react-error-boundary";
import logo from "assets/images/img_10M_logo_normal.png";
import { colors } from "constants/colors";
import { ERROR_MESSAGE, GO_HOME_MESSAGE } from "constants/common";
import { GlobalLanguage, MicroLanguageListType } from "types/Common";
import { HeaderCancelButtonComponent } from "components/molecules/Header";

const ErrorFallback = ({ error }: FallbackProps) => {
  const [lang, setLang] = useState<GlobalLanguage>("ko");
  const [isGlobal, setIsGlobal] = useState(false);
  const isApp = typeof window !== "undefined" && !!window.ReactNativeWebView; // 웹/앱 구분
  const title = isGlobal ? "40weeks later" : "열달후에";
  const errorCode = (error as any)?.response?.status || (error as any)?.code; // 에러코드
  const errorItem: MicroLanguageListType =
    ERROR_MESSAGE.find((item) => item.code === errorCode) || ERROR_MESSAGE[0];

  useEffect(() => {
    const browserLang =
      typeof navigator !== "undefined"
        ? navigator.language.toLowerCase()
        : "ko"; // 브라우저 언어 설정 확인

    browserLang.startsWith("ja") && setLang("ja");
    browserLang.startsWith("en") && setLang("en");
  }, [navigator.language]);

  useEffect(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: "error",
        errorMsg: "error fallback " + error,
      }),
    );
  }, [error]);

  useEffect(() => {
    lang !== "ko" && setIsGlobal(true);
  }, [lang]);

  return (
    <StyledContainer $isApp={isApp}>
      {isApp ? (
        <HeaderCancelButtonComponent title="" />
      ) : (
        <Header>
          <Logo $isGlobal={isGlobal}>{title}</Logo>
        </Header>
      )}
      <StyledContent>
        <StyledErrorImage src={require("assets/images/img_404error.png")} />
        <StyledTitleText>{errorItem.title[lang]}</StyledTitleText>
        <p style={{ whiteSpace: "pre-wrap" }}>{errorItem.text[lang]}</p>
        <ButtonType1 onClick={() => window.history.back()}>
          {GO_HOME_MESSAGE[lang]}
        </ButtonType1>
      </StyledContent>
      <Footer>
        <FooterInner>
          <FooterLogo>{title}</FooterLogo>
          <address>Copyright © Connect i Co.,Ltd. All Rights Reserved.</address>
        </FooterInner>
      </Footer>
    </StyledContainer>
  );
};

export default ErrorFallback;

const Header = styled.div`
  background-color: ${colors.white};
`;

const Logo = styled.h1<{ $isGlobal: boolean }>`
  width: 108px;
  height: 50px;
  background: url(${logo}) no-repeat 50% / 100%;
  font-size: 0;
  line-height: 0;
  text-indent: 100%;
  -webkit-transition: 0.3s;
  transition: 0.3s;
`;

const StyledContent = styled.div`
  padding: 30px 20px;
  text-align: center;
`;

const StyledErrorImage = styled.img`
  width: 120px;
  transition: 0.3s;
`;

const StyledTitleText = styled.p`
  padding-top: 20px;
  padding-bottom: 4px;
  font-size: 17px;
  font-weight: 500;
  line-height: 25px;
  letter-spacing: -0.85px;
`;

const ButtonType1 = styled.button`
  display: none;
  height: 47px;
  margin-top: 32px;
  padding: 0 40px;
  background: ${colors.mainPurple};
  border-radius: 16px;
  font-size: 15px;
  font-weight: 500;
  line-height: 47px;
  letter-spacing: -0.75px;
  color: ${colors.white};
  -webkit-transition: 0.3s;
  transition: 0.3s;
`;

const Footer = styled.div`
  display: none;
  width: 100%;
  padding: 30px 20px 50px;
  background-color: ${colors.lightGray1};
  box-sizing: border-box;
  address {
    padding-top: 8px;
    font-size: 11px;
    line-height: 20px;
    letter-spacing: 0;
    color: ${colors.gray};
  }
`;

const FooterInner = styled.div`
  max-width: 1240px;
  margin: 0 auto;
`;

const FooterLogo = styled.span`
  display: block;
  width: 88px;
  height: 34px;
  background-color: ${colors.gray};
  mask: url(${logo}) no-repeat 50% / 100%;
  font-size: 0;
  line-height: 0;
  text-indent: 100%;
  transition: 0.3s;
`;

const StyledContainer = styled.section<{ $isApp: boolean }>`
  display: flex;
  height: 100vh;
  flex-direction: column;
  justify-content: space-between;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: -0.7px;

  ${({ $isApp }) =>
    $isApp
      ? css`
          justify-content: center;
        `
      : css`
          ${Header} {
            padding: 10px 16px;
          }
          ${ButtonType1} {
            display: inline-block;
          }
          ${Footer} {
            display: block;
          }
          @media only screen and (min-width: 760px) {
            font-size: 24px;
            line-height: 36px;
            letter-spacing: -1.2px;
            ${Header} {
              padding-top: 20px;
              padding-bottom: 20px;
            }
            ${Logo} {
              width: 150px;
              height: 58px;
            }
            ${StyledErrorImage} {
              width: 200px;
            }
            ${StyledTitleText} {
              padding-top: 40px;
              padding-bottom: 8px;
              font-size: 40px;
              line-height: 58px;
            }
            ${ButtonType1} {
              height: 74px;
              margin-top: 40px;
              font-size: 24px;
              line-height: 74px;
              letter-spacing: -1.2px;
            }
            ${FooterLogo} {
              display: inline-block;
              width: 100px;
              height: 48px;
              vertical-align: middle;
            }
            address {
              display: inline-block;
              padding-left: 16px;
              padding-top: 0;
              font-size: 16px;
              line-height: 24px;
            }
          }
        `}
`;
